import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { API_CONFIG } from '../config/apiConfig';
import { ENDPOINTS } from '../config/endpoints';
import type { ApiError, ApiResponse } from '../types/common.types';
import { getErrorMessage } from '../utils';

const apiClient = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.headers,
});

// ---------- Request Interceptor ----------
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Attach auth token from localStorage (authStore persists here)
    const authStorage = localStorage.getItem('zelog-auth-storage');
    if (authStorage) {
      try {
        const parsed = JSON.parse(authStorage);
        const accessToken = parsed?.state?.accessToken;
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
      } catch {
        // Ignore parse errors
      }
    }

    // Attach workspace ID
    const workspaceStorage = localStorage.getItem('zelog-workspace-storage');
    if (workspaceStorage) {
      try {
        const parsed = JSON.parse(workspaceStorage);
        const workspaceId = parsed?.state?.activeWorkspaceId;
        if (workspaceId) {
          config.headers['X-Workspace-Id'] = workspaceId;
        }
      } catch {
        // Ignore parse errors
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ---------- Response Interceptor ----------
let isRefreshing = false;
let refreshRetryCount = 0;
const MAX_REFRESH_RETRIES = 3;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

const forceLogout = () => {
  refreshRetryCount = 0;
  localStorage.removeItem('zelog-auth-storage');
  window.location.href = '/login';
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 — attempt token refresh (up to MAX_REFRESH_RETRIES times)
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            reject,
          });
        });
      }

      if (refreshRetryCount >= MAX_REFRESH_RETRIES) {
        forceLogout();
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      isRefreshing = true;
      refreshRetryCount += 1;

      try {
        const authStorage = localStorage.getItem('zelog-auth-storage');
        const parsed = authStorage ? JSON.parse(authStorage) : null;
        const refreshToken = parsed?.state?.refreshToken;

        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await axios.post<
          ApiResponse<{ accessToken: string; refreshToken: string }>
        >(`${API_CONFIG.baseURL}${ENDPOINTS.AUTH.REFRESH}`, { refreshToken });

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data.data;

        // Update stored tokens and reset retry counter on success
        if (parsed) {
          parsed.state.accessToken = newAccessToken;
          parsed.state.refreshToken = newRefreshToken;
          localStorage.setItem('zelog-auth-storage', JSON.stringify(parsed));
        }
        refreshRetryCount = 0;

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        if (refreshRetryCount >= MAX_REFRESH_RETRIES) {
          forceLogout();
        } else {
          localStorage.removeItem('zelog-auth-storage');
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Normalize error response
    const apiError: ApiError = {
      status: error.response?.status || 500,
      message:
        (error.response?.data as { message?: string })?.message ||
        error.message ||
        'An unexpected error occurred',
      errors: (error.response?.data as { errors?: Record<string, string[]> })?.errors,
      code: (error.response?.data as { code?: string })?.code,
    };

    apiError.message = getErrorMessage(apiError);

    return Promise.reject(apiError);
  },
);

export { apiClient };
